import React from "react";
import { Dialog } from "@headlessui/react";
import { Building2, Mail, PencilLine, ShieldCheck, UserCircle2, X } from "lucide-react";
import { useTheme } from "./ThemeProvider";

export default function ProfileModal({ open, onClose, onEditProfile, user, displayName, roleLabel, customerName }) {
    const { currentTheme } = useTheme();

    return (
        <Dialog open={open} onClose={onClose} className="fixed inset-0 z-[100]" data-modal-back="true">
            <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-sm" aria-hidden="true" />

            <div className="fixed inset-0 overflow-y-auto px-3 py-4 sm:px-4 sm:py-6">
                <div className="flex min-h-full items-start justify-center sm:items-center">
                <Dialog.Panel className="relative my-auto w-full max-w-md overflow-hidden rounded-[24px] border border-white/70 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.22)]">
                    <div
                        className="absolute inset-x-0 top-0 h-24"
                        style={{
                            background: `linear-gradient(135deg, ${currentTheme.color}16, rgba(248,250,252,0.95) 54%, rgba(255,255,255,0) 100%)`,
                        }}
                    />

                    <button
                        data-modal-close
                        onClick={onClose}
                        className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                        aria-label="Close profile"
                    >
                        <X className="h-4.5 w-4.5" />
                    </button>

                    <div className="relative p-5 sm:p-6">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
                            <UserCircle2 className="h-3.5 w-3.5" />
                            Profile
                        </div>

                        <div className="mt-5 flex items-center gap-4 rounded-[20px] border border-slate-200/80 bg-white/85 p-3 shadow-sm">
                            <div className="relative shrink-0">
                                <div
                                    className="absolute inset-0 rounded-full blur-xl opacity-35"
                                    style={{ background: currentTheme.color }}
                                />
                                <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border-4 border-white bg-slate-100 shadow-[0_12px_28px_rgba(15,23,42,0.16)]">
                                    {user?.profile_image ? (
                                        <img src={user.profile_image} alt="Profile" className="h-full w-full object-cover" />
                                    ) : (
                                        <UserCircle2 className="h-9 w-9 text-slate-400" />
                                    )}
                                </div>
                            </div>

                            <div className="min-w-0 flex-1 text-left">
                                <Dialog.Title className="truncate text-xl font-semibold tracking-tight text-slate-900">
                                    {displayName}
                                </Dialog.Title>
                                <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                                    <ShieldCheck className="h-3.5 w-3.5" />
                                    {roleLabel}
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 grid gap-2.5">
                            {customerName ? (
                                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-3.5 py-3 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-sm"
                                            style={{ background: currentTheme.color }}
                                        >
                                            <Building2 className="h-4.5 w-4.5" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                                Customer Name
                                            </p>
                                            <p className="truncate text-sm font-semibold text-slate-900">
                                                {customerName}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-3.5 py-3 shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm">
                                        <Mail className="h-4.5 w-4.5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Email
                                        </p>
                                        <p className="truncate text-sm font-semibold text-slate-900">
                                            {user?.email || "No email available"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-3.5 py-3 shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm">
                                        <ShieldCheck className="h-4.5 w-4.5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Access Level
                                        </p>
                                        <p className="truncate text-sm font-semibold text-slate-900">
                                            {roleLabel}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                            <button
                                type="button"
                                onClick={onEditProfile}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white"
                            >
                                <PencilLine className="h-4 w-4" />
                                Edit Profile
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-2xl py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(15,23,42,0.16)] transition hover:-translate-y-0.5 hover:opacity-90"
                                style={{ background: currentTheme.color }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
}
